import React, { useState, useEffect, useRef, useMemo } from 'react';
import useFetchICEData from './Hooks/useFetchICEData';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { Helmet } from 'react-helmet';
import { Redirect } from 'react-router-dom';

// api
import entityApi from '/src/apis/entityApi';
import { WEB_URL } from '/src/lib/endpoints'

// helpers
import { getItemWebUrl, deserializeData } from '/src/lib/helpers';
import { HUBSPOT_FORM_SOURCE, ENTITY_TYPE } from '/src/lib/constants';
import { MAGNITT_ACCESS_ROLES } from '/src/lib/constants/userRoles';
import { INVESTOR_TYPE } from '/src/lib/constants/investorType'
import { handleRedirectMessage } from '/src/components/Reusable/toast';

//page reusable componenets
import ProfileNavigationBar from './Reusable/ProfileNavigationBar'
import CompanyOverview from './Reusable/CompanyOverview';
import Skeleton from 'react-loading-skeleton';
import StartupsSection from './Reusable/StartupsSection';
import { FundingTable } from './Reusable/FundingRounds/FundingTableHandler';
import InvestmentsSection from '/src/components/ICESections/Investments/Investments';
import TeamMembersSection from '/src/components/TeamMembers/TeamMembers';
import ContactSalesPopup from '/src/components/ContactForms/ContactSalesPopup';
import ManagedFundsSection  from '/src/components/ICESections/ManagedFunds/ManagedFunds';
import InvestorProfileHeader from '/src/components/ICESections/ProfileHeader/ProfileHeader'
import ICEHighlights from '/src/components/ICESections/ICEHighlights/ICEHighlights'
import ActivityMetrics from '/src/components/ICESections/ActivityMetrics/ActivityMetrics'
import FundsInvestedSection from '/src/components/ICESections/FundsInvestedIn/FundsInvestedSection'
import PortfolioExitsSection from '/src/components/ICESections/PortfolioExits/PortfolioExits'

const ICEPage = (props) => {
    const { staticContext, match: { params }, entityType, user, history } = props;
    let investorData = {};

    if (__isBrowser__) {
        investorData = deserializeData(window.__INITIAL_DATA__)
        delete window.__INITIAL_DATA__
    }
    else {
        investorData = deserializeData(staticContext.data)
    }

    const [investor, setInvestor] = useState(investorData);
    const [requestedFundId, setRequestedFundId] = useState(null);

    const slug = params.slug.split('-');
    const entityId = parseInt(slug.pop())
    const userRoles = user?.roles || [];
    const hasAccess = user?.is_loggedin && userRoles.includes(MAGNITT_ACCESS_ROLES.directory_access)

    const { restrictedProfileData = {}, isLoading: isEntityLoading } = useFetchICEData(entityId, entityType, hasAccess)
    const investorUrl = getItemWebUrl('investors', investor?.id, investor?.code);
    let metaDescription;
    if (investor?.description) {
        metaDescription = investor.description.replace(/<\/?[^>]+(>|$)/g, "");
        metaDescription = metaDescription.replace(/&nbsp;/g, ' ');
        if (metaDescription !== undefined && metaDescription !== null && metaDescription.length > 300) {
            metaDescription = metaDescription.substr(0, 300);
            metaDescription = metaDescription.substr(0, metaDescription.lastIndexOf(' ')) + ' ...';
        }
    }
    const [state, setState] = useState({
        related: [],
        rounds: [],
        startups: [],
        industries: [],

        funding: false,
        mentorship: false,
        legal: false,
        act_co_found: false,
        it: false,
        marketing: false,
        act_board: false,
        select: '',
        textarea: '',
        owner: false,
        isTeamMember: '',

        views: 0,
        userroles: false,

        loggedIn: false,

        name: '',
        email: '',
        message: '',

        cv: null,
        cvFile: '',
        cvError: '',

        letter: null,
        letterFile: '',
        letterError: '',

        search: '',
        errorLogin: false,

        emailError: '',
        currentuser: '',

        // contact sales popup source
        contactSalesPopUpSource: HUBSPOT_FORM_SOURCE.single_profile_locked_sections
    });
    const investorHeaderRef = useRef(null)
    const investorEntityData = useMemo(() => ({
        ...investor,
        ...restrictedProfileData
    }), [investor, restrictedProfileData]);
    const isPEInvestor = investorEntityData?.type_data?.id === INVESTOR_TYPE.private_equity || (investorEntityData?.number_of_pe_investments ?? 0) > 0;
    const isEntityTypeInvestor =
        entityType.toLowerCase() === ENTITY_TYPE.investor.toLowerCase()

    //TODO: move API calls to hooks, useFectchICEData
    const getUserTeamStatus = () => {

        // avoid check if user is logged out
        if (!user.is_loggedin) {
            return;
        }

        entityApi.getUserTeamStatus(entityId, entityType).then(
            response => {
                if (!isEmpty(response) && response.is_team_member == 1) {
                    setState(prev => ({
                        ...prev,
                        owner: true,
                        isTeamMember: true
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        isTeamMember: false
                    }))
                }
            }
        );
    }

    const sectionRefs = {
        overview: {
            ref: useRef(null),
            name: 'Overview'
        },
        funding: {
            ref: useRef(null),
            name: 'Funding',
            componentsIds: ['section-funding', 'section-fundingrounds']
        },
        investments: {
            ref: useRef(null),
            name: 'Direct Investments',
            componentsIds: [
                'section-investorinvestments',
            ],
            hidSectionConditions: [!isEntityLoading && !investorEntityData?.number_of_investments]
        },
        fundsInvested: {
            ref: useRef(null),
            name: 'Fund Investments',
            componentsIds: ['section-investmentsinfunds']
        },
        startups: {
            ref: useRef(null),
            name: 'Startups',
            componentsIds: ['section-startup-relations'],
            hidSectionConditions: [isEntityTypeInvestor]
        },
        funds: {
            ref: useRef(null),
            name: 'Managed Funds',
            componentsIds: ['section-managedfunds']
        },
        exits: {
            ref: useRef(null),
            name: 'Exits',
            componentsIds: ['section-portfolioexits']
        },
        teams: {
            ref: useRef(null),
            name: 'Teams',
            componentsIds: ['section-teams']
        }
    }

    useEffect(() => {
        if (typeof window === 'undefined') return

        const headerEl = investorHeaderRef.current
        if (!headerEl) return

        const initialTop = headerEl.getBoundingClientRect().top + window.scrollY

        const updatePosition = () => {
            const scrollY = window.scrollY || window.pageYOffset || 0

            if (scrollY > initialTop) {
                headerEl.classList.add('investor-header--sticky')
            } else {
                headerEl.classList.remove('investor-header--sticky')
            }
        }

        updatePosition()

        window.addEventListener('scroll', updatePosition)
        window.addEventListener('resize', updatePosition)

        return () => {
            window.removeEventListener('scroll', updatePosition)
            window.removeEventListener('resize', updatePosition)
            if (headerEl) {
                headerEl.classList.remove('investor-header--sticky')
            }
        }
    }, [])

    useEffect(() => {
        handleRedirectMessage(props.location);
    }, []);

    useEffect(() => {
        getUserTeamStatus();
    }, [user?.is_loggedin])


    if (isEmpty(investor) || investor.id === null) {
        return <Redirect to="/not-found" />
    }

    const handleFundLinkClick = (fundId) => {
        setRequestedFundId(fundId);
        sectionRefs.investments.ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const fundingTableProps = {
        entityData: investorEntityData,
        entityType,
        entityId,
        isTeamMember: state.isTeamMember,
        // true while getUserTeamStatus() hasn't resolved yet (sentinel = empty string)
        isTeamMemberLoading: user?.is_loggedin && state.isTeamMember === '',
        user,
        owner: state.owner,
        isPEInvestor,
        setState: (newState) => setState(prev => ({ ...prev, ...newState })),
    };

    return (
        <div className="footer-margin-offset">
            <div className="sice-page ">
                <Helmet
                    title={`${investor.name} | MAGNiTT`}
                    link={[
                        {
                            rel: "canonical",
                            href: `${WEB_URL}/${investorUrl}`
                        }
                    ]}
                    meta={[
                        {
                            name: "description",
                            content: metaDescription
                        },
                        {
                            name: "abstract",
                            content: metaDescription
                        },
                        {
                            property: "og:type",
                            content: "article"
                        },
                        {
                            property: "og:url",
                            content: `${WEB_URL}/${investorUrl}`
                        },
                        {
                            property: "og:title",
                            content: investor.name
                        }
                    ]}
                    script={[
                        {
                            type: "application/ld+json",
                            innerHTML: `{
                                "@context": "http://schema.org",
                                "@type": "BreadcrumbList",
                                "itemListElement": [{
                                    "@type": "ListItem",
                                    "position": 1,
                                    "name": "investors",
                                    "item": "${WEB_URL}/investors"
                                },
                                {
                                    "@type": "ListItem",
                                    "position": 2,
                                    "name": "${investor.name}",
                                    "item": "${WEB_URL}/${investorUrl}"
                                }]
                            }`
                        }
                    ]}
                />
                <div
                    className="investor-profile-card investor-header"
                    ref={investorHeaderRef}
                >
                    <InvestorProfileHeader
                        investor={investorEntityData}
                        owner={state.owner}
                        entityId={entityId}
                        entityType={entityType}
                        setState={(newState) =>
                            setState(prev => ({ ...prev, ...newState }))
                        }
                        page={
                            HUBSPOT_FORM_SOURCE.single_profile_locked_contacts
                        }
                    />
                    <ProfileNavigationBar
                        sectionRefs={sectionRefs}
                        entityType={entityType}
                        variant="investor"
                        isLoading={isEntityLoading}
                    />
                </div>
                <div className=" content-box">
                    <div className="right-side investor-main-column">
                        {isEntityLoading
                            ? (
                                <div className="profile-section-box company-overview" ref={sectionRefs.overview.ref}>
                                    <div className="general-overview general-overview--investor">
                                        <div className="general-overview__content">
                                            <h2 className="profile-section-heading">General Overview</h2>
                                            <Skeleton height={14} style={{ marginBottom: 10 }} />
                                            <Skeleton height={14} width="92%" style={{ marginBottom: 10 }} />
                                            <Skeleton height={14} width="88%" style={{ marginBottom: 10 }} />
                                            <Skeleton height={14} width="94%" style={{ marginBottom: 10 }} />
                                            <Skeleton height={14} width="72%" />
                                        </div>
                                    </div>
                                    <div className="general-overview__second_section">
                                        <div className="general-overview__metrics">
                                            {[1, 2, 3, 4].map(n => (
                                                <div key={n} className="metric" style={{ marginBottom: 16 }}>
                                                    <Skeleton width={110} height={13} />
                                                    <Skeleton width={120} height={15} />
                                                </div>
                                            ))}
                                        </div>
                                        <div style={{ flex: '1 1 50%' }}>
                                            <Skeleton height={13} width="70%" style={{ marginBottom: 10 }} />
                                            <Skeleton height={28} style={{ marginBottom: 6 }} />
                                            <Skeleton height={28} style={{ marginBottom: 6 }} />
                                            <Skeleton height={28} />
                                        </div>
                                    </div>
                                </div>
                            )
                            : <CompanyOverview
                                entityDetails={{ ...investorEntityData, isPEInvestor }}
                                entityType={entityType}
                                sectionref={sectionRefs.overview.ref}
                                user={user}
                            />
                        }

                        <>
                            <ICEHighlights
                                entity={investorEntityData}
                                user={user}
                                showInfo={hasAccess}
                                isEntityLoading={isEntityLoading}
                                entityId={entityId}
                                entityType={entityType}
                            />
                            <ActivityMetrics
                                entity={investorEntityData}
                                user={user}
                                showInfo={hasAccess}
                                isEntityLoading={isEntityLoading}
                            />
                        </>

                        {/* Investment Section Tables */}
                        <div
                            ref={sectionRefs.investments.ref}
                            id="section-investments"
                        >
                            <FundingTable
                                {...fundingTableProps}
                                type="fundingRounds"
                            />


                            <InvestmentsSection
                                {...fundingTableProps}
                                type="investorInvestments"
                                requestedFundId={requestedFundId}
                                onFundIdConsumed={() => setRequestedFundId(null)}
                            />

                            <FundsInvestedSection
                                entityId={entityId}
                                entityType={entityType}
                                user={user}
                                entityData={investorEntityData}
                                setState={(newState) => setState(prev => ({ ...prev, ...newState }))}
                            />
                        </div>

                        <StartupsSection
                            user={user}
                            entityId={entityId}
                            entityType={entityType}
                            investor={investorEntityData}
                            sectionRef={sectionRefs.startups.ref}
                        />

                        <ManagedFundsSection
                            ref={sectionRefs.funds.ref}
                            fundingTableProps={fundingTableProps}
                            onFundClick={handleFundLinkClick}
                        />

                        <PortfolioExitsSection
                            ref={sectionRefs.exits.ref}
                            {...fundingTableProps}
                        />

                        <TeamMembersSection
                            entityType={entityType}
                            entityId={entityId}
                            user={user}
                            entityName={investor?.name}
                            page={
                                HUBSPOT_FORM_SOURCE.single_profile_locked_contacts
                            }
                            sectionref={sectionRefs.teams.ref}
                        />
                    </div>
                </div>
            </div>
            {/* used for rendering popup when user clicks contact sales button */}
            <ContactSalesPopup page={state.contactSalesPopUpSource} />
        </div>
    );
}

const mapStateToProps = (state) => ({
    user: state.userReducer
})

export default connect(mapStateToProps, null)(ICEPage);
